'use client';

import { cn } from '@fshr/shared-react';
import Icon from '../icon/icon';
import { IconCardProps } from './types';
import { useTranslations } from '@fshr/context/i18n-context';

const LOADING_DOT_DELAYS = ['0s', '0.2s', '0.4s'];

export default function IconCard({
	icon,
	label,
	data,
	cta,
	isLoading,
	isDataLoading,
}: IconCardProps) {
	const t = useTranslations();

	return (
		<div
			className={cn(
				'flex h-full flex-col items-center justify-center rounded-none bg-warm-stone-20 spacing-card-padding-xs',
				!isLoading && 'lg:justify-start'
			)}
		>
			{isLoading ? (
				<div
					className="flex h-[22px] w-full items-center justify-center gap-[5px]"
					aria-live="polite"
					aria-busy="true"
				>
					{LOADING_DOT_DELAYS.map(animationDelay => (
						<span
							key={animationDelay}
							className="h-[6px] w-[6px] animate-pulse rounded-full bg-text-heading"
							style={{ animationDelay }}
						/>
					))}
				</div>
			) : (
				<div className="flex w-full flex-col items-center spacing-stack-s">
					<Icon icon={icon} className="text-[32px] leading-none text-text-primary" />

					<div className="flex w-full flex-col items-center spacing-stack-xs">
						<span className="text-center text-subtitle2 text-text-heading">{t(label)}</span>

						{isDataLoading ? (
							<div
								className="flex h-[22px] w-full items-center justify-center gap-[5px]"
								aria-live="polite"
								aria-busy="true"
							>
								{LOADING_DOT_DELAYS.map(animationDelay => (
									<span
										key={animationDelay}
										className="h-[6px] w-[6px] animate-pulse rounded-full bg-text-heading"
										style={{ animationDelay }}
									/>
								))}
							</div>
						) : (
							<span className="text-center text-body3 text-text-heading">{t(data)}</span>
						)}

						{cta && (
							<button
								type="button"
								onClick={cta.onClick}
								className="text-button2 text-button-primary underline underline-offset-[50%] decoration-solid [text-decoration-skip-ink:none] [text-decoration-thickness:0.1em] hover:text-button-primary-hover"
							>
								{t(cta.label)}
							</button>
						)}
					</div>
				</div>
			)}
		</div>
	);
}
